import { enqueueSnackbar } from 'notistack';
import { useRef, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import {
  IconX,
  IconUpload,
  IconFolder,
  IconRefresh,
  IconChevronDown,
} from '@tabler/icons-react';

import Button from '@/components/Button/Button';
import Input from '@/components/Input/Input';
import TextArea from '@/components/TextArea';
import { useFormatText } from '@/hooks/useFormatText';
import usePageTitle from '@/hooks/usePageTitle';
import { getFaqs } from '@/service/faqService';
import { uploadBulkImages, submitSchoolReport } from '@/service/supportService';
import { useSettingsStore } from '@/store/useSettingsStore';

type TFaqItem = {
  id: string;
  question: string;
  answer: string;
};

const HelpSupport = () => {
  const { formatText } = useFormatText();
  const { setTitle } = usePageTitle();
  const language = useSettingsStore((state) => state.settings.language);
  const [expandedFaqIndex, setExpandedFaqIndex] = useState(0);
  const [faqItems, setFaqItems] = useState<TFaqItem[]>([]);
  const [formKey, setFormKey] = useState(0);
  const [assets, setAssets] = useState<
    {
      id: string;
      file: File;
      url: string;
      status: 'uploading' | 'success' | 'failed';
    }[]
  >([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm({
    defaultValues: { title: '', issue: '' },
  });

  const fetchFaqs = async () => {
    try {
      const response = await getFaqs();
      if (response?.data) {
        setFaqItems(response.data);
      } else {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error);
    }
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleFiles = async (newFiles: File[]) => {
    const validFiles = newFiles.filter((file) =>
      /^image\/(jpeg|jpg|png|heic|avif)$/.exec(file.type)
    );

    if (validFiles.length === 0) {
      if (newFiles.length > 0) {
        enqueueSnackbar(
          formatText('help-support.allowed-file-types') ||
            'Only JPEG, PNG, HEIC, and AVIF files are allowed',
          {
            variant: 'error',
          }
        );
      }
      return;
    }

    setIsUploading(true);

    // Add files to state with 'uploading' status
    const tempIds = validFiles.map(() => crypto.randomUUID());
    const newUploadingAssets = validFiles.map((file, i) => ({
      file,
      id: tempIds[i], // Temporary ID to track this specific upload attempt
      url: URL.createObjectURL(file),
      status: 'uploading' as const,
    }));

    setAssets((prev) => [...prev, ...newUploadingAssets]);

    try {
      const fileFormData = new FormData();
      validFiles.forEach((file) => {
        fileFormData.append('images', file);
      });
      fileFormData.append('folder', 'reports-image');

      const uploadResponse = await uploadBulkImages(fileFormData);
      let assetIds: string[] = [];

      if (uploadResponse?.status) {
        // As per the provided response format, data is an array of strings
        if (Array.isArray(uploadResponse.data)) {
          assetIds = uploadResponse.data;
        } else if (uploadResponse.data.asset_ids) {
          assetIds = uploadResponse.data.asset_ids;
        } else if (uploadResponse.data.id) {
          assetIds = [uploadResponse.data.id];
        }
      } else if (uploadResponse && Array.isArray(uploadResponse)) {
        assetIds = uploadResponse.map((item: { id?: string } | string) =>
          typeof item === 'object' && item.id ? item.id : String(item)
        );
      } else if (uploadResponse?.id) {
        assetIds = [uploadResponse.id];
      } else {
        enqueueSnackbar(uploadResponse.message, { variant: 'error' });
        setAssets((prev) =>
          prev.map((asset) =>
            tempIds.includes(asset.id as ReturnType<typeof crypto.randomUUID>)
              ? { ...asset, status: 'failed' }
              : asset
          )
        );
        return assets;
      }

      // Update the status of these specific files to 'success'
      setAssets((prev) =>
        prev.map((asset) => {
          const matchedIndex = tempIds.indexOf(
            asset.id as ReturnType<typeof crypto.randomUUID>
          );
          if (matchedIndex !== -1) {
            return {
              ...asset,
              id: assetIds[matchedIndex] || asset.id,
              status: 'success',
            };
          }
          return asset;
        })
      );
    } catch (error) {
      console.error('Upload Error', error);
      enqueueSnackbar(
        formatText('help-support.upload-failed') || 'Failed to upload images',
        { variant: 'error' }
      );
      // Update the status to 'failed'
      setAssets((prev) =>
        prev.map((asset) =>
          tempIds.includes(asset.id as ReturnType<typeof crypto.randomUUID>)
            ? { ...asset, status: 'failed' }
            : asset
        )
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRetryUpload = (assetInfo: { file: File; id: string }) => {
    // Remove the failed one first
    setAssets((prev) => prev.filter((a) => a.id !== assetInfo.id));
    // Re-trigger the upload
    handleFiles([assetInfo.file]);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFiles(Array.from(e.target.files));
    }
  };

  const removeFile = (index: number) => {
    setAssets((prev) => {
      const newAssets = [...prev];
      const removed = newAssets.splice(index, 1)[0];
      if (removed?.url) {
        URL.revokeObjectURL(removed.url);
      }
      return newAssets;
    });
  };

  const onSubmit = async (data: { title: string; issue: string }) => {
    try {
      const successfulAssets = assets.filter((a) => a.status === 'success');
      const asset_ids = successfulAssets.map((a) => a.id);

      const response = await submitSchoolReport({
        issue: data.title,
        description: data.issue,
        asset_ids,
      });

      if (response?.status) {
        reset();
        setFormKey((prev) => prev + 1);
        assets.forEach((a) => {
          if (a.url) URL.revokeObjectURL(a.url);
        });
        setAssets([]);
        enqueueSnackbar(formatText('help-support.report-submitted'), {
          variant: 'success',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    }
  };

  useEffect(() => {
    setTitle(formatText('help-support.page-title'));
    fetchFaqs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  return (
    <div className="w-full max-w-216 mx-auto flex flex-col gap-10">
      {faqItems?.length > 0 && (
        <section className="flex flex-col gap-4">
          <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
            {formatText('help-support.faq-title')}
          </h2>

          <div className="flex flex-col gap-4">
            {faqItems.map((item, index) => {
              const isExpanded = index === expandedFaqIndex;

              return (
                <div
                  key={item.id}
                  className="border border-neutral-light-200 rounded-xl overflow-hidden transition-all duration-300 ease-in-out"
                >
                  <button
                    type="button"
                    className={`w-full h-13 px-4 flex items-center justify-between border-b border-neutral-light-200 cursor-pointer ${
                      isExpanded
                        ? 'bg-neutral-light-100'
                        : 'bg-white border-b-0'
                    }`}
                    onClick={() => setExpandedFaqIndex(isExpanded ? -1 : index)}
                  >
                    <p className="text-sm font-semibold text-neutral-primary text-start">
                      {`${index + 1}. ${item.question}`}
                    </p>

                    <span className="shrink-0 p-2 rounded-lg border border-neutral-light-200 bg-white shadow-s">
                      <IconChevronDown
                        size={16}
                        className={`text-neutral-secondary transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`}
                      />
                    </span>
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${
                      isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="flex w-full items-center p-4 faq-answer-container">
                        <div
                          className="text-sm font-normal leading-5 text-neutral-primary prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{ __html: item.answer }}
                          style={{
                            wordBreak: 'break-word',
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-4 px-1">
        <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
          {formatText('help-support.issue-title')}
        </h2>

        <div className="flex flex-col gap-2">
          <p className="text-base font-semibold text-neutral-secondary">
            {formatText('help-support.issue-title-label')}
          </p>
          <Input
            name="title"
            register={register}
            registerOptions={{
              required:
                formatText('help-support.issue-title-required') ||
                'This field is required',
            }}
            error={errors.title?.message as string}
            placeholder={formatText('help-support.issue-title-placeholder')}
            className="w-full"
            inputClassName="text-sm font-normal text-neutral-primary placeholder:text-neutral-disabled"
          />
        </div>

        <div className="flex flex-col gap-2">
          <p className="text-base font-semibold text-neutral-secondary">
            {formatText('help-support.issue-description-label')}
          </p>
          <TextArea
            key={formKey}
            name="issue"
            register={register}
            registerOptions={{
              required:
                formatText('help-support.issue-description-required') ||
                'This field is required',
            }}
            error={errors.issue?.message as string}
            placeholder={formatText('help-support.issue-placeholder')}
            maxLength={250}
            showCount
            rows={4}
            className="w-full"
            textAreaClassName="text-sm font-normal text-neutral-primary placeholder:text-neutral-disabled"
          />
        </div>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-xl font-semibold text-neutral-primary leading-7 tracking-tight">
          {formatText('help-support.attachments-title')}
        </h2>

        <div
          className={`w-full rounded-[10px] border border-dashed transition-colors duration-200 cursor-pointer ${
            isDragging
              ? 'border-brand-primary bg-brand-light-50'
              : 'border-neutral-light-200 bg-white'
          } p-8 flex flex-col items-center gap-4 text-center`}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <IconUpload size={24} className="text-neutral-secondary" />

          {isUploading && (
            <p className="text-sm font-semibold text-brand-secondary">
              {formatText('help-support.uploading') || 'Uploading...'}
            </p>
          )}

          <div className="flex flex-col gap-1">
            <p className="text-sm font-semibold text-neutral-primary">
              {formatText('help-support.upload-title')}
            </p>
            <p className="text-xs text-neutral-primary">
              {formatText('help-support.upload-subtitle')}
            </p>
          </div>

          <Button
            variant="unstyled"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-brand-light-50 text-xs text-brand-secondary"
            onClick={(e) => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            icon={<IconFolder size={16} />}
            label={formatText('help-support.browse-files')}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/avif,image/jpg"
            className="hidden"
            multiple
            onChange={handleFileSelect}
          />
        </div>

        {assets.length > 0 && (
          <div className="flex flex-col gap-2">
            {assets.map((asset, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 border rounded-lg bg-white ${
                  asset.status === 'failed'
                    ? 'border-red-500 bg-red-50'
                    : 'border-neutral-light-200'
                }`}
              >
                <div className="flex flex-col gap-1 w-full overflow-hidden">
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center gap-2 overflow-hidden flex-1">
                      {asset.file.type.startsWith('image/') ? (
                        <div className="relative shrink-0">
                          <img
                            src={asset.url}
                            alt={asset.file.name}
                            className={`w-8 h-8 object-cover rounded ${
                              asset.status === 'uploading' ? 'opacity-50' : ''
                            }`}
                          />
                        </div>
                      ) : (
                        <IconFolder
                          size={20}
                          className="text-neutral-secondary shrink-0"
                        />
                      )}
                      <span className="text-sm text-neutral-primary truncate">
                        {asset.file.name}
                      </span>
                      <span className="text-xs text-neutral-secondary shrink-0">
                        ({(asset.file.size / 1024).toFixed(1)} KB)
                      </span>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {asset.status === 'failed' && (
                        <Button
                          variant="unstyled"
                          className="flex items-center gap-1 text-xs font-semibold text-brand-secondary hover:underline"
                          onClick={() => handleRetryUpload(asset)}
                          label={formatText('help-support.retry') || 'Retry'}
                          icon={<IconRefresh size={16} />}
                        />
                      )}
                      {asset.status === 'uploading' && (
                        <span className="text-xs text-neutral-secondary italic">
                          {formatText('help-support.uploading') ||
                            'Uploading...'}
                        </span>
                      )}
                      <Button
                        variant="unstyled"
                        onClick={() => removeFile(index)}
                        className="p-1 text-neutral-secondary hover:text-red-500 transition-colors ml-2"
                        icon={<IconX size={16} />}
                      />
                    </div>
                  </div>
                  {asset.status === 'failed' && (
                    <span className="text-xs text-red-500">
                      {formatText('help-support.upload-failed-retry') ||
                        'Failed to upload the image. Please try again.'}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      <div>
        <Button
          className="w-fit"
          label={formatText('help-support.send-request')}
          onClick={handleFormSubmit(onSubmit)}
          loading={isSubmitting || isUploading}
          disabled={isSubmitting || isUploading}
        />
      </div>
    </div>
  );
};

export default HelpSupport;
